import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axios, { AxiosError } from 'axios';
import toastr from 'toastr';
import { formatFirmName } from './firmName';
import AccessTypeOptions from './AccessTypeOptions';

type Props = {
  distro: { id: string; name: string; healthClaimDistroId: string };
  userList: string[];
  onClose: (changed: boolean) => void;
};

const AddFirmUserModal = ({ distro, userList, onClose }: Props) => {
  const [isClientOnly, setIsClientOnly] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [note, setNote] = useState('');
  const [sendWelcome, setSendWelcome] = useState(false);
  const [saving, setSaving] = useState(false);

  const trimmedEmail = email.trim();
  const isExistingUser = userList.some(x => x?.toLowerCase() === trimmedEmail.toLowerCase());
  const namesRequired = trimmedEmail !== '' && !isExistingUser;
  const canSave = trimmedEmail !== '' && note.trim() !== '' && (!namesRequired || (firstName.trim() !== '' && lastName.trim() !== ''));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.post('/api/Advisors/createdistrouser', {
        distroId: distro.id,
        email: trimmedEmail,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        isClientOnly,
        note: note.trim(),
        sendWelcome,
      });
      if (!sendWelcome) {
        toastr.success(`Added ${trimmedEmail} to ${distro.name}. No welcome email was sent; use Send Welcome when ready.`);
      } else if (res.data && res.data.emailSent === false) {
        toastr.success(`Added ${trimmedEmail} to ${distro.name}.`);
        toastr.warning(`Access was granted, but the welcome email to ${trimmedEmail} failed to send. Use Send Welcome to retry.`);
      } else {
        toastr.success(`Added ${trimmedEmail} to ${distro.name} and sent the welcome email.`);
      }
      onClose(true);
    } catch (e) {
      const err = e as AxiosError;
      toastr.error(typeof err.response?.data === 'string' ? err.response.data : 'Failed to add the user.');
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={() => onClose(false)} centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Add User</Modal.Title>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {formatFirmName(distro)} ·{isClientOnly ? 'No access to financials or commission statements' : 'Can view commission statements'}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Access type <span className="text-danger">*</span></Form.Label>
          <AccessTypeOptions name="add-access-type" isClientOnly={isClientOnly} onChange={setIsClientOnly} />
        </Form.Group>
        {isClientOnly
          ? <Alert variant="info" style={{ fontSize: 13 }}>Users given <strong>Client-Only Firm Access</strong> have entire firm client scope, but have <strong>no access to financials or commission statements</strong>.</Alert>
          : <Alert variant="warning" style={{ fontSize: 13 }}>Users given <strong>Full Firm Access</strong> have entire firm financial and client scope and <strong>can view commission statements</strong> for all advisors at {distro.name}. Add carefully.</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Email address <span className="text-danger">*</span></Form.Label>
          <Form.Control type="email" value={email} onChange={e => setEmail(e.target.value)} />
        </Form.Group>
        {namesRequired && (
          <div className="d-flex gap-2">
            <Form.Group className="mb-3 flex-fill">
              <Form.Label>First name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={firstName} onChange={e => setFirstName(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3 flex-fill">
              <Form.Label>Last name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={lastName} onChange={e => setLastName(e.target.value)} />
            </Form.Group>
          </div>
        )}
        <Form.Group>
          <Form.Label>Ticket number or comment <span className="text-danger">*</span></Form.Label>
          <Form.Control value={note} maxLength={500} onChange={e => setNote(e.target.value)} />
        </Form.Group>
        <div className="RT-Checkbox mt-3">
          <label className="RT-Checkbox__inner">
            <input
              type="checkbox"
              className="RT-Checkbox__input"
              checked={sendWelcome}
              onChange={e => setSendWelcome(e.target.checked)}
            />
            <span className="RT-Checkbox__faux">
              <svg viewBox="0 0 52 52">
                <path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"></path>
              </svg>
            </span>
            <span className="RT-Checkbox__label">Send welcome email</span>
          </label>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="primary" disabled={!canSave || saving} onClick={save}>{sendWelcome ? 'Save & send invite' : 'Save'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddFirmUserModal;
